/**
 *  @class
 *  @function FacetsToggle
 */
class FacetToggle extends HTMLElement {

  constructor() {
    super();
    this.init();
  }
  init() {
    this.drawer = document.getElementById('Facet-Drawer');
    this.container = this.closest('.facets--bar-sticky');

    if (!this.drawer) return;

    // Add functionality to buttons
    this.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDrawer();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleDrawer();
      }
    });

    if (this.drawer.dataset.facetControlsReady !== 'true') {
      this.drawer.dataset.facetControlsReady = 'true';
      this.drawer.querySelector('[data-facet-apply]')?.addEventListener('click', () => this.toggleDrawer(false));
      this.drawer.querySelector('.side-panel-close')?.addEventListener('click', () => this.updateExpandedState(false));
      document.querySelector('.click-capture')?.addEventListener('click', () => this.updateExpandedState(false));
    }

    if (this.container) {
      this.enableSticky();
    }

  }
  updateExpandedState(isOpen) {
    document.querySelectorAll('facet-toggle[aria-controls="Facet-Drawer"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }
  toggleDrawer(forceOpen) {
    const returnFocus = document.querySelector('facet-toggle[aria-controls="Facet-Drawer"][aria-expanded="true"]');
    const isOpen = typeof forceOpen === 'boolean'
      ? forceOpen
      : !this.drawer.classList.contains('active');

    document.body.classList.toggle('open-cc', isOpen);
    this.drawer.classList.toggle('active', isOpen);
    this.updateExpandedState(isOpen);

    if (isOpen) {
      window.setTimeout(() => this.drawer.querySelector('.side-panel-close')?.focus(), 0);
    } else if (returnFocus?.isConnected) {
      returnFocus.focus();
    } else if (this.isConnected) {
      this.focus();
    }
  }
  enableSticky() {
    let rootMargin = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) + 1;
    this.observer = new IntersectionObserver(
      ([e]) => {
        e.target.classList.toggle('is-pinned', e.intersectionRatio <= 1 && e.intersectionRatio > 0);
      },
      {
        threshold: [1],
        rootMargin: `-${rootMargin}px 0px 0px 0px`
      }
    );

    this.observer.observe(this.container);
  }

}
customElements.define('facet-toggle', FacetToggle);

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.id === 'compare_toggle') {
        return;
      }
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      // Strict-search pagination stores its own history state. Facets should not
      // turn that state into a request containing "undefined" search params.
      if (event.state?.grStrictSearchPage) {
        FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
        return;
      }

      const stateSearchParams = event.state?.searchParams;
      const searchParams = typeof stateSearchParams === 'string'
        ? stateSearchParams
        : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const isSearchPage = window.location.pathname.includes('/search');
    const requestId = ++FacetFiltersForm.latestRequestId;

    // Only the newest filter/sort request is allowed to update the page. This
    // prevents a slower, older response from replacing a newer result set.
    if (FacetFiltersForm.activeController) FacetFiltersForm.activeController.abort();
    FacetFiltersForm.activeController = new AbortController();

    productGridContainer?.querySelector('.collection')?.classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    // Update the address bar before rendering. Gear Rack's strict search reads
    // the current URL when it rebuilds the final search result set.
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      // Search results are post-processed by gr-relevance-search.js. Reusing
      // cached section HTML here can restore an older grid, so search always
      // gets fresh Shopify section HTML. Collection pages keep their cache.
      if (!isSearchPage && FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId);
      } else {
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event,
          requestId,
          FacetFiltersForm.activeController.signal,
          !isSearchPage
        );
      }
    });
  }

  static renderSectionFromFetch(url, event, requestId, signal, shouldCache = true) {
    fetch(url, { signal, credentials: 'same-origin', cache: 'no-store' })
      .then(response => {
        if (!response.ok) throw new Error(`Filter request failed with ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.latestRequestId) return;

        const html = responseText;
        if (shouldCache) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData
            .filter((entry) => entry.url !== url)
            .concat({ html, url });
        }

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.notifyRenderComplete(requestId);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (requestId !== FacetFiltersForm.latestRequestId) return;
        console.warn('Filters could not be refreshed:', error);
        document.querySelector('#ProductGridContainer .collection')?.classList.remove('loading');
        document.querySelectorAll('.thb-filter-count').forEach((item) => item.classList.remove('loading'));
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.latestRequestId) return;
    const cachedEntry = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cachedEntry) return;

    const html = cachedEntry.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.notifyRenderComplete(requestId);
  }

  static notifyRenderComplete(requestId) {
    if (requestId !== FacetFiltersForm.latestRequestId) return;
    window.dispatchEvent(new CustomEvent('gear-rack:facets-rendered', {
      detail: {
        requestId,
        searchParams: FacetFiltersForm.searchParamsPrev,
        isSearchPage: window.location.pathname.includes('/search')
      }
    }));
  }

  static renderProductGridContainer(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const activeRow = event ? event.target.closest('collapsible-row') : null;
    const activeFormId = activeRow?.closest('form')?.id;

    facetDetailsElements.forEach((element) => {
      const sourceFormId = element.closest('form')?.id;
      if (!sourceFormId) return;

      const target = document.querySelector(`#${sourceFormId} collapsible-row[data-index="${element.dataset.index}"]`);
      if (!target) return;

      const isActiveRow = activeRow && activeFormId === sourceFormId && activeRow.dataset.index === element.dataset.index;
      if (isActiveRow) {
        FacetFiltersForm.renderCounts(element, target);
      } else {
        target.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count', '.mobile-filter-selection-count'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      if (!source) return;
      document.querySelectorAll(selector).forEach((target) => {
        target.innerHTML = source.innerHTML;
        target.className = source.className;
      });
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.availability') === '') {
      searchParams.delete('filter.v.availability');
    }
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.latestRequestId = 0;
FacetFiltersForm.activeController = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function Facetremove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.compareToggle = this.querySelector('#compare_toggle');
    if (this.compareToggle) {
      this.compareToggle.addEventListener('change', this.onCompare);
    }
  }
  onCompare(e) {
    document.body.classList.toggle('compare-true');
    e.preventDefault();
    return false;
  }
}
customElements.define('facet-toolbar', FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);
